import { tns } from 'tiny-slider/src/tiny-slider';

// Instance-specific interval storage to prevent conflicts between multiple widgets
const widgetIntervals = new Map();

// Minimal DRY helpers - only for the most repetitive patterns
const getStoryElements = (id) => ({
  parent: document.querySelector(`#${id}__story-card`),
  player: document.querySelector(`#${id}__story-card video`),
  stories: document.querySelectorAll(`#${id}__story-card .story`),
  playBtn: document.querySelector(`#${id}__story-card .play-button`),
});

const setPlayButton = (btn, show) => btn && Object.assign(btn.style, {
  display: show ? 'flex' : 'none',
  opacity: show ? 1 : 0,
  zIndex: show ? '2' : '-1',
});

const playVideo = (player) => player.play().catch(e => console.error('Error playing video:', e));

// Helper function to manage video progress intervals per widget instance
function videoProgress(widgetId, player, story, callback) {
  // Clear only this widget's existing interval
  if (widgetIntervals.has(widgetId)) {
    clearInterval(widgetIntervals.get(widgetId));
    widgetIntervals.delete(widgetId);
  }

  const interval = setInterval(function() {
    // Check if video is paused - don't clear interval, just skip this cycle
    if (player.paused) {
      return;
    }

    const percentage = Math.round((player.currentTime / player.duration) * 100);
    if (percentage > 0) {
      story.value = percentage;
      if (story.value >= 99) {
        clearInterval(interval);
        widgetIntervals.delete(widgetId);
        callback(null, true);
      }
    }
  }, 100);

  // Store interval specific to this widget instance
  widgetIntervals.set(widgetId, interval);
}

/**
 * One-side-widget specific story execution - NEVER pauses, always loops
 */
const storyExecution = (id, action = null) => {
  const { parent, player, stories, playBtn } = getStoryElements(id);

  if (!player) {
    return; // Prevent errors if video element doesn't exist
  }

  if (parent.attributes.initialized.value === 'false') {
    parent.setAttribute('initialized', 'true');
  }

  // Index of current video playing
  if (!player.attributes.current) {
    player.setAttribute('current', '0');
  }
  let index = Number(player.attributes.current.value) || 0;
  const totalStories = stories.length;

  // Actions handler
  if (action === 'next') {
    index = Number(player.attributes.current.value);
    if (index >= totalStories - 1) {
      // At the last video - ALWAYS loop back to start and continue playing (NEVER pause)
      index = 0;
      player.setAttribute('current', index);
      stories.forEach(story => (story.value = 0));
      player.currentTime = 0;
      // Don't return early - continue to set up the first video properly
    } else {
      index = Number(player.attributes.current.value) + 1;
      if (stories.length > 0) {
        stories[index - 1].value = 100;
      }
    }
  } else if (action === 'previous') {
    index = Number(player.attributes.current.value);
    if (index === 0) {
      index = totalStories - 1;
      player.setAttribute('current', index);
      for (let i = 0; i < index; i++) {
        stories[i].value = 100;
      }
    } else {
      index = Number(player.attributes.current.value) - 1;
      if (stories.length > 0) {
        stories[index + 1].value = 0;
      }
    }
  } else if (action === 'reset') {
    // Clean up any existing intervals for this widget
    cleanupWidgetIntervals(id);

    index = 0;
    player.currentTime = 0;
    player.pause(); // Pause on reset to show play button
    player.setAttribute('current', index);
    setPlayButton(playBtn, true); // Show play button on reset
    stories.forEach(story => (story.value = 0));
    // Set the first video source and make it active
    if (stories.length > 0) {
      player.src = stories[0].attributes.url.value;
      stories[0].classList.add('active');
      stories.forEach((story, i) => {
        if (i !== 0) {
          story.classList.remove('active');
        }
      });
    }
    return;
  } else if (action && action.match(/^\d+$/)) {
    // Handle numeric actions dynamically (1, 2, 3, etc.)
    const targetIndex = parseInt(action) - 1;
    if (targetIndex >= 0 && targetIndex < totalStories) {
      index = targetIndex;
      player.setAttribute('current', index);
      stories.forEach((story, i) => {
        story.value = i < index ? 100 : 0; // Completed vs current/future videos
      });
    }
  }

  // Defines the updated current story to be played
  const currentStory = stories[index];

  // Resets the component if at end or beginning
  if (index >= stories.length || index < 0) {
    return storyExecution(id, 'reset');
  }

  // Progress bar active toggling
  stories.forEach(story => story.classList.remove('active'));
  currentStory.classList.add('active');

  // Dynamically sets the source of the video playing
  if (stories.length > 0) {
    player.src = currentStory.attributes.url.value;
  }
  player.setAttribute('current', index);
  playVideo(player);

  // Triggers progress on progress bar
  if (stories.length > 0) {
    videoProgress(id, player, currentStory, (error, result) => {
      if (!error) {
        storyExecution(id, 'next');
      }
    });
  } else {
    // For single video without progress bars, handle the ended event directly
    player.onended = () => {
      player.currentTime = 0;
      playVideo(player);
    };
  }
};

/**
 * One-side-widget specific playStories - simplified for auto-play behavior
 */
const playStories = (event, id) => {
  const { parent, player, stories, playBtn } = getStoryElements(id);

  if (!player) {
    return;
  }

  // Handle pause/play toggle for one-side-widget
  if (event && event.target && event.target.tagName && event.target.tagName !== 'OBJECT') {
    // This is a real click event, handle pause/play toggle
    const isActuallyPlaying = !player.paused && !player.ended && player.currentTime > 0 && player.readyState >= 2;

    if (player.paused || player.ended || !isActuallyPlaying) {
      // Video is paused, ended, or not really playing, so play it
      setPlayButton(playBtn, false);
      playVideo(player);
    } else {
      // Video is playing, so pause it
      player.pause();
      setPlayButton(playBtn, true);
      return;
    }
  }

  // Binds the events on first interaction with the story card (initialization only)
  if (parent.attributes.initialized.value === 'false') {
    // For single video, start playing automatically
    if (stories.length === 0) {
      playVideo(player);
    }

    player.addEventListener('play', () => {
      // Only hide play button for multiple videos, not the video container for single videos
      if (event && event.target && stories.length > 0) {
        event.target.style.display = 'none';
        event.target.style.opacity = 0;
        event.target.style.zIndex = '-1';
      }
    });

    player.addEventListener('ended', () => {
      // For one-side-widget, just loop back to start
      player.currentTime = 0;
      playVideo(player);
    });
  }

  // Pagination click handlers - only set up handlers for stories that exist
  const storiesArray = Array.from(stories);
  storiesArray.forEach((story, index) => {
    if (story && story.id) {
      const storyElement = document.getElementById(story.id);
      if (storyElement) {
        storyElement.onclick = function(e) {
          e.stopPropagation();
          storyExecution(id, (index + 1).toString());
        };
      }
    }
  });
};

// Utility function to clean up intervals for a specific widget
const cleanupWidgetIntervals = (widgetId) => {
  if (widgetIntervals.has(widgetId)) {
    clearInterval(widgetIntervals.get(widgetId));
    widgetIntervals.delete(widgetId);
  }
};

// Global function for one-side-widget story cards
window.groupeNordik = window.groupeNordik || {};
window.groupeNordik.$toggleStoryVideo = (event, id) => {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }
  playStories(event, id);
};

// Export cleanup function for use in page navigation/cleanup
window.groupeNordik.cleanupWidgetIntervals = cleanupWidgetIntervals;

window.groupeNordik.oneSideSlider = {
  init: (el) => {
    tns({
      container: el.querySelector('.w-one__slider'),
      items: 1,
      mode: 'gallery',
      autoplayButtonOutput: false,
      controls: false,
      nav: false,
      mouseDrag: true,
      autoplay: true,
      speed: 1800,
    });

    // Initialize story functionality
    const storyCard = el.querySelector('.story-card');
    if (storyCard) {
      const storyId = storyCard.getAttribute('story');
      if (storyId) {
        const video = document.querySelector(`#${storyId}__story-card video`);
        const progressBars = el.querySelectorAll('.story');

        if (video) {
          // Set video attributes for autoplay
          video.setAttribute('muted', 'true');
          video.setAttribute('playsinline', 'true');

          if (progressBars.length === 0) {
            // Single video: just initialize playStories for click handling and autoplay
            playStories(null, storyId);
          } else {
            // Multiple videos: use storyExecution for proper progress tracking
            playStories(null, storyId);
            storyExecution(storyId);
          }
        }
      }
    }
  },
};
